import { formatCurrency, formatUnit } from '@/common/utils/formatters';
import { unFormatCurrency } from '@/common/utils/unFormatters';
import { useMemo, useState } from 'react';

export interface ProposalModificationInput {
  new_unit_price?: number | null
  new_warranty_in_days?: number | null
}

interface UseProposalModificationParams {
  modification?: ProposalModificationInput
  unitPrice: string
  warrantyInDays: number
  quantity: number | string
  totalValue: string
  disabled?: boolean
  onSave?: (newUnitPrice: string, newWarrantyInDays: string) => void
}

export const useProposalModification = ({
  modification,
  unitPrice,
  warrantyInDays,
  quantity,
  totalValue,
  disabled = false,
  onSave,
}: UseProposalModificationParams) => {
  const [isOpen, setIsOpen] = useState(false)
  const [localUnitPrice, setLocalUnitPrice] = useState('')
  const [localWarranty, setLocalWarranty] = useState('')

  const originalUnitPrice = Number(unitPrice) || 0
  const originalWarranty = warrantyInDays
  const quantityNumber = Number(quantity) || 0
  const originalTotalValue = Number(totalValue) || 0

  const newUnitPriceNumber = modification?.new_unit_price ?? null
  const newWarrantyNumber = modification?.new_warranty_in_days || null

  const isUnitPriceModified = newUnitPriceNumber !== null && newUnitPriceNumber !== originalUnitPrice
  const isWarrantyModified = newWarrantyNumber !== null && newWarrantyNumber !== originalWarranty

  const displayNewUnitPrice = isUnitPriceModified ? newUnitPriceNumber : null
  const displayNewWarranty = isWarrantyModified ? newWarrantyNumber : null

  const displayNewTotalValue = useMemo(() => {
    if (!isUnitPriceModified || newUnitPriceNumber === null) return null
    return newUnitPriceNumber * quantityNumber
  }, [isUnitPriceModified, newUnitPriceNumber, quantityNumber])

  const unitPricePercentage = useMemo(() => {
    if (displayNewUnitPrice === null || originalUnitPrice === 0) return null
    return ((displayNewUnitPrice - originalUnitPrice) / originalUnitPrice).toString()
  }, [displayNewUnitPrice, originalUnitPrice])

  const totalValuePercentage = useMemo(() => {
    if (displayNewTotalValue === null || originalTotalValue === 0) return null
    return ((displayNewTotalValue - originalTotalValue) / originalTotalValue).toString()
  }, [displayNewTotalValue, originalTotalValue])

  const previewTotalValue = useMemo(() => {
    const parsed = unFormatCurrency(localUnitPrice)
    return (parsed || 0) * quantityNumber
  }, [localUnitPrice, quantityNumber])

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setLocalUnitPrice(newUnitPriceNumber !== null ? formatCurrency(newUnitPriceNumber) : formatCurrency(originalUnitPrice))
      setLocalWarranty(newWarrantyNumber !== null ? formatUnit(newWarrantyNumber, 'dia(s)') : formatUnit(originalWarranty, 'dia(s)'))
    }
    setIsOpen(open)
  }

  const onConfirm = () => {
    onSave?.(localUnitPrice, localWarranty)
    setIsOpen(false)
  }

  return {
    disabled,
    isOpen,
    handleOpenChange,
    localUnitPrice,
    setLocalUnitPrice,
    localWarranty,
    setLocalWarranty,
    previewTotalValue,
    onConfirm,
    originalUnitPrice,
    displayNewUnitPrice,
    unitPricePercentage,
    originalTotalValue,
    displayNewTotalValue,
    totalValuePercentage,
    originalWarranty,
    displayNewWarranty,
  }
}
